import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import '../styles/Auth.css';

function Login() {
  const navigate = useNavigate();
  const { login, register, loading } = useAuth();
  const [mode, setMode] = useState('login');   // login | register | forgot | reset
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [keepSigned, setKeepSigned] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);

  // Forgot / reset state
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [tokenInput, setTokenInput] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (mode === 'register') {
        if (!username.trim()) { setError('Username is required'); return; }
        if (!displayName.trim()) { setError('Display name is required'); return; }
        const result = await register(username.trim(), displayName.trim(), email.trim(), password);
        if (result?.pending) {
          setPendingApproval(true);
          return;
        }
        navigate('/');
      } else {
        await login(email.trim(), password);
        navigate('/');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setForgotLoading(true);
    try {
      const res = await fetch('http://localhost:8080/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (data.reset_token) {
        setResetToken(data.reset_token);
        setTokenInput(data.reset_token);
        setMode('reset');
      } else {
        // Email not found — show generic message, stay on forgot
        setError('If that email is registered, a reset token will appear here. Check your details and try again.');
      }
    } catch {
      setError('Request failed. Make sure the server is running.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    setResetLoading(true);
    try {
      const res = await fetch('http://localhost:8080/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenInput.trim(), new_password: newPassword }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResetSuccess(true);
      } else {
        setError(data.detail || 'Reset failed. Token may be invalid or expired.');
      }
    } catch {
      setError('Request failed. Make sure the server is running.');
    } finally {
      setResetLoading(false);
    }
  };

  const goLogin = () => {
    setMode('login'); setError(''); setForgotEmail('');
    setResetToken(''); setTokenInput(''); setNewPassword(''); setResetSuccess(false);
  };

  return (
    <div className="login-split">
      {/* ═══ Left Hero Panel ═══ */}
      <aside className="login-hero">
        <div className="login-hero-inner">
          {/* Brand */}
          <div className="login-brand">
            <svg width="28" height="28" viewBox="0 0 16 16" fill="none">
              <rect y="1" width="16" height="2.4" rx="1.2" fill="#818cf8"/>
              <rect y="6.8" width="12" height="2.4" rx="1.2" fill="#6366f1"/>
              <rect y="12.6" width="8" height="2.4" rx="1.2" fill="#4f46e5"/>
            </svg>
            <span className="login-brand-name">Synth<em>loom</em></span>
          </div>

          {/* Tagline */}
          <div className="login-tagline">
            <span className="login-label">SYNTHETIC DATA PLATFORM</span>
            <h1>
              Generate <em className="login-italic">realistic</em> data at any scale.
            </h1>
            <p className="login-hero-desc">
              Define schemas, generate thousands of rows, validate quality — and export clean CSV files. Built for developers, analysts, and QA teams who need reliable test data fast.
            </p>
          </div>

          {/* Feature cards */}
          <div className="login-features">
            <div className="login-feature">
              <div className="login-feature-icon icon-teal">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <rect x="3" y="3" width="14" height="14" rx="2" />
                  <polyline points="7,7 9,9 7,11" /><line x1="10" y1="10" x2="14" y2="10" />
                  <line x1="7" y1="14" x2="13" y2="14" />
                </svg>
              </div>
              <div className="login-feature-text">
                <strong>Design Studio</strong>
                <span>Design data models with typed fields — strings, numbers, dates, enums and more. Save and reuse across runs.</span>
              </div>
            </div>

            <div className="login-feature">
              <div className="login-feature-icon icon-indigo">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <circle cx="10" cy="10" r="3" />
                  <path d="M10 2v2m0 12v2m-5.66-3.34 1.42-1.42m8.48-8.48 1.42-1.42M2 10h2m12 0h2m-3.34 5.66-1.42-1.42M5.76 5.76 4.34 4.34" />
                </svg>
              </div>
              <div className="login-feature-text">
                <strong>Data Generator</strong>
                <span>Load a config, set row count, run the 4-step pipeline — validate, generate, verify, save.</span>
              </div>
            </div>

            <div className="login-feature">
              <div className="login-feature-icon icon-slate">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <rect x="2" y="2" width="16" height="16" rx="2" />
                  <line x1="2" y1="7" x2="18" y2="7" /><line x1="2" y1="12" x2="18" y2="12" />
                  <line x1="7" y1="7" x2="7" y2="18" /><line x1="13" y1="7" x2="13" y2="18" />
                </svg>
              </div>
              <div className="login-feature-text">
                <strong>Data Viewer</strong>
                <span>Browse, search, and filter every generated dataset. Export any view as a CSV in one click.</span>
              </div>
            </div>

            <div className="login-feature">
              <div className="login-feature-icon icon-amber">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M4 15l4-4 3 3 5-6" />
                  <rect x="2" y="2" width="16" height="16" rx="2" />
                </svg>
              </div>
              <div className="login-feature-text">
                <strong>Generation History</strong>
                <span>Track every run — dataset name, elapsed time, validation status, and file counts at a glance.</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="login-stats">
            <div className="login-stat">
              <span className="login-stat-n">50+</span>
              <span className="login-stat-l">FIELD TYPES</span>
            </div>
            <div className="login-stat">
              <span className="login-stat-n">10k</span>
              <span className="login-stat-l">ROWS PER RUN</span>
            </div>
            <div className="login-stat">
              <span className="login-stat-n">CSV</span>
              <span className="login-stat-l">EXPORT READY</span>
            </div>
            <div className="login-stat">
              <span className="login-stat-n">ER</span>
              <span className="login-stat-l">DIAGRAM</span>
            </div>
          </div>
        </div>
      </aside>

      {/* ═══ Right Form Panel ═══ */}
      <main className="login-form-panel">
        <div className="login-form-inner">
          {pendingApproval ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px', padding: '48px 16px', textAlign: 'center', height: '100%' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" width="56" height="56">
                <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
              </svg>
              <div>
                <h2 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: '24px', color: 'var(--color-text-primary)', margin: '0 0 10px' }}>Account request submitted</h2>
                <p style={{ fontFamily: 'var(--font-sans)', fontSize: '14px', color: 'var(--color-text-secondary)', maxWidth: '340px', lineHeight: '1.65', margin: 0 }}>
                  Your registration is awaiting admin approval. You will be able to sign in once an admin approves your account.
                </p>
              </div>
              <button
                className="login-submit"
                style={{ marginTop: '8px', width: 'auto', padding: '11px 28px' }}
                onClick={() => { setPendingApproval(false); setMode('login'); setError(''); }}
              >
                ← Back to sign in
              </button>
            </div>
          ) : mode === 'forgot' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div className="login-form-header">
                <span className="login-welcome-label">PASSWORD RECOVERY</span>
                <h2>Reset your password</h2>
                <p>Enter your email address and we'll generate a reset token for you.</p>
              </div>
              <form onSubmit={handleForgot} className="login-form">
                <div className="login-field">
                  <label>EMAIL</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@synthloom.com"
                    autoComplete="email"
                    required
                  />
                </div>
                {error && <div className="auth-error">{error}</div>}
                <button type="submit" className="login-submit" disabled={forgotLoading}>
                  {forgotLoading ? 'Generating…' : 'Get reset token →'}
                </button>
              </form>
              <div className="login-switch">
                <span><button onClick={goLogin}>← Back to sign in</button></span>
              </div>
            </div>
          ) : mode === 'reset' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {resetSuccess ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', padding: '32px 16px', textAlign: 'center' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" width="48" height="48">
                    <circle cx="12" cy="12" r="10"/><polyline points="8,12 11,15 16,9"/>
                  </svg>
                  <div>
                    <h2 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: '22px', color: 'var(--color-text-primary)', margin: '0 0 8px' }}>Password updated!</h2>
                    <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: 0 }}>You can now sign in with your new password.</p>
                  </div>
                  <button className="login-submit" style={{ width: 'auto', padding: '11px 28px' }} onClick={goLogin}>Sign in →</button>
                </div>
              ) : (
                <>
                  <div className="login-form-header">
                    <span className="login-welcome-label">SET NEW PASSWORD</span>
                    <h2>Enter your reset token</h2>
                    <p>Copy the token below and set a new password. Token expires in 60 minutes.</p>
                  </div>
                  {resetToken && (
                    <div style={{ background: 'var(--color-surface-raised, #f1f5f9)', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <code style={{ flex: 1, fontSize: '12px', wordBreak: 'break-all', color: 'var(--color-text-primary)', fontFamily: 'monospace' }}>{resetToken}</code>
                      <button
                        type="button"
                        style={{ flexShrink: 0, fontSize: '12px', padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--color-border)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
                        onClick={() => navigator.clipboard.writeText(resetToken)}
                      >Copy</button>
                    </div>
                  )}
                  <form onSubmit={handleReset} className="login-form">
                    <div className="login-field">
                      <label>RESET TOKEN</label>
                      <input
                        type="text"
                        value={tokenInput}
                        onChange={(e) => setTokenInput(e.target.value)}
                        placeholder="Paste your reset token"
                        required
                      />
                    </div>
                    <div className="login-field">
                      <label>NEW PASSWORD</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Min 8 characters"
                        autoComplete="new-password"
                        required
                      />
                    </div>
                    {error && <div className="auth-error">{error}</div>}
                    <button type="submit" className="login-submit" disabled={resetLoading}>
                      {resetLoading ? 'Updating…' : 'Set new password →'}
                    </button>
                  </form>
                  <div className="login-switch">
                    <span><button onClick={goLogin}>← Back to sign in</button></span>
                  </div>
                </>
              )}
            </div>
          ) : (<>
          <div className="login-form-header">
            <span className="login-welcome-label">WELCOME BACK</span>
            <h2>{mode === 'login' ? 'Sign in to Synthloom' : 'Create your account'}</h2>
            <p>
              {mode === 'login'
                ? 'Enter your credentials to access your workspace.'
                : 'Register to start creating synthetic datasets.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {mode === 'register' && (
              <>
                <div className="login-field">
                  <label>USERNAME</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="synthloom_user"
                    autoComplete="username"
                  />
                </div>
                <div className="login-field">
                  <label>DISPLAY NAME</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Synthloom User"
                    autoComplete="name"
                  />
                </div>
              </>
            )}

            <div className="login-field">
              <label>EMAIL</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@synthloom.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="login-field">
              <label>PASSWORD</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
              />
            </div>

            {mode === 'login' && (
              <div className="login-extras">
                <label className="login-check">
                  <input type="checkbox" checked={keepSigned} onChange={(e) => setKeepSigned(e.target.checked)} />
                  Keep me signed in
                </label>
                <button type="button" className="login-forgot" onClick={() => { setMode('forgot'); setForgotEmail(email); setError(''); }}>Forgot password?</button>
              </div>
            )}

            {error && <div className="auth-error">{error}</div>}

            <button type="submit" className="login-submit" disabled={loading}>
              {loading
                ? 'Please wait…'
                : mode === 'login' ? 'Sign in →' : 'Create account →'}
            </button>
          </form>

          <div className="login-divider">
            <span>or continue with</span>
          </div>

          <button className="login-google" disabled>
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </button>

          <div className="login-switch">
            {mode === 'login' ? (
              <span>Don't have an account? <button onClick={() => { setMode('register'); setError(''); }}>Request access</button></span>
            ) : (
              <span>Already have an account? <button onClick={() => { setMode('login'); setError(''); }}>Sign in</button></span>
            )}
          </div>
          </>)}
        </div>
      </main>
    </div>
  );
}

export default Login;
